import { assignTextStyle, CARD_BASE_HEIGHT, React, variantProvider } from '@/app'
import { CardBase, CardBaseProps, Icon, Image, View, Text, ProgressBar } from '@/components'
import { APIClient } from '@/services'
import { Module } from '@/types'
import { clamp, useIsMobile } from '@/utils'
import { arePropsEqual, useI18N } from '@codeleap/common'

const ICON_SIZE = 12
const IMAGE_WIDTH = '25%'

export type ModuleCardProps = Partial<Omit<CardBaseProps, 'debugName'>> & {
  module: Module
  width?: number
}

const ModuleCardComponent = (props: ModuleCardProps) => {
  const { module: initialData, disabled, ...rest } = props

  const { t } = useI18N()
  const isMobile = useIsMobile()

  const query = APIClient.Learn.modulesManager.useRetrieve({ id: initialData.id, queryOptions: { initialData } })
  const module = query.data

  const hasMultipleChapters = module?.chapters?.length > 1

  const labelChapters = t(
    `learn.components.cards.module.${hasMultipleChapters ? 'chaptersIndicator' : 'chapterIndicator'}`
  )

  const image = React.useMemo(() => {
    if (isMobile) return module?.mobile_image ?? module?.image
    return module?.web_image ?? module?.image
  }, [isMobile])

  const progress = React.useMemo(() => clamp(0, Math.round(module?.progress * 10), 100), [module?.progress])

  return (
    <CardBase
      variants={['card:collection', module?.is_placeholder && 'card:faded', disabled && 'card:disabled']}
      debugName="Module card"
      gap={null}
      {...rest}
    >
      {image ? (
        <View style={styles.imageWrapper}>
          <Image objectFit="cover" source={image} style={styles.image} />
        </View>
      ) : null}

      <View
        variants={['column', 'alignStart', 'justifyStart', 'padding:2', 'fullWidth', 'flex', 'fullHeight']}
        style={{ maxWidth: `calc(100% - ${image ? IMAGE_WIDTH : ''})` }}
      >
        <View variants={['row', 'fullWidth', 'justifySpaceBetween', 'alignCenter']}>
          <View>
            <View variants={['row', 'center']}>
              <Icon debugName="Module card icon" name="book" size={ICON_SIZE} variants={['neutral5']} />
              <Text
                variants={['p4', 'color:neutral5', 'marginLeft:0.5']}
                text={`${module?.chapters?.length} ${labelChapters}`}
              />
            </View>
            {module?.is_new ? (
              <Text text={t('learn.components.cards.module.isNewBadge')} style={styles.isNewBadge} />
            ) : null}
          </View>

          <View style={styles.progressBar}>
            <ProgressBar progress={progress} text={`${progress}%`} />
          </View>
        </View>

        <Text variants={['h5', 'color:neutral6', 'marginTop:1']} style={styles.text} text={module?.title} />
      </View>
    </CardBase>
  )
}

export const ModuleCard = React.memo(ModuleCardComponent, (previous, next) => {
  return arePropsEqual(previous, next, { check: ['module'] })
})

const styles = variantProvider.createComponentStyle(
  theme => ({
    imageWrapper: {
      height: 'auto',
      minWidth: IMAGE_WIDTH,
      maxWidth: IMAGE_WIDTH,
      flex: 1,
      overflow: 'hidden',
      maxHeight: CARD_BASE_HEIGHT.collection,
      minHeight: CARD_BASE_HEIGHT.collection
    },
    image: {
      width: '100%',
      height: '100%',
      minHeight: '100%',
      borderBottomLeftRadius: theme.borderRadius.small,
      borderTopLeftRadius: theme.borderRadius.small
    },
    progressBar: {
      ...theme.presets.center,
      marginRight: theme.spacing.value(0.5)
    },
    text: {
      overflow: 'hidden',
      display: '-webkit-box',
      '-webkit-box-orient': 'vertical',
      '-webkit-line-clamp': '2'
    },
    isNewBadge: {
      backgroundColor: theme.colors.primary3,
      ...assignTextStyle('p2')(theme).text,
      color: theme.colors.neutral1,
      borderRadius: theme.borderRadius.small,
      ...theme.spacing.paddingHorizontal(0.75),
      ...theme.spacing.marginLeft(0.5)
    }
  }),
  true
)
